import React, { useState } from "react";
import { useCheckbox, Chip, VisuallyHidden, tv } from "@heroui/react";
import { FaCheck } from "react-icons/fa6";

interface CheckboxActiveProps {
  children?: React.ReactNode;
  defaultSelected?: boolean;
  onChange?: (isChecked: boolean) => void;
  textValueOn: string;
  textValueOff: string;
}

const checkbox = tv({
  slots: {
    base: "border-default hover:bg-default-200",
    content: "text-default-500 text-xs",
  },
  variants: {
    isSelected: {
      true: {
        base: "border-vistagreen bg-vistagreen/70 hover:bg-vistagreen/50 hover:border-vistagreen",
        content: "text-primary-foreground pl-1",
      },
    },
    isFocusVisible: {
      true: {
        base: "outline-none ring-2 ring-focus ring-offset-2 ring-offset-background",
      },
    },
  },
});

export default function CheckboxActive({
  children,
  defaultSelected,
  onChange,
  textValueOn,
  textValueOff,
}: Readonly<CheckboxActiveProps>) {
  const {
    isSelected,
    isFocusVisible,
    getBaseProps,
    getLabelProps,
    getInputProps,
  } = useCheckbox({
    defaultSelected: defaultSelected,
  });

  const styles = checkbox({ isSelected, isFocusVisible });
  const [selected, setSelected] = useState(defaultSelected);

  const handleChipClick = () => {
    const newSelected = !selected;
    setSelected(newSelected);
    if (onChange) {
      onChange(newSelected);
    }
  };

  return (
    <label {...getBaseProps()}>
      <VisuallyHidden>
        <input {...getInputProps()} />
      </VisuallyHidden>
      <Chip
        classNames={{
          base: styles.base(),
          content: styles.content(),
        }}
        color="primary"
        startContent={isSelected ? <FaCheck className="ml-1" /> : null}
        variant="faded"
        onClick={handleChipClick}
        {...getLabelProps()}
      >
        {selected ? textValueOn : textValueOff}
      </Chip>
    </label>
  );
}